/**
 * TRACI-1106: the earlier conversation a new or reset agent session needs to pick up where
 * the shopper left off.
 *
 * The agent's memory lives on the server and can expire or be cleared (12 hour TTL, a
 * deploy's auto-clear, a lost session). The chat on screen survives in this browser, so the
 * browser sends its OWN stored conversation with a message when the server may have lost it.
 * The backend only uses it when that shopper's server memory really is empty, and never reads
 * another session's history, so nothing here can attach one shopper's chat to another's.
 */
import type { Message } from '../types/chat.types';
import { safeRedact } from '../utils/redactCritical';

export interface PriorTurn {
  role: 'user' | 'assistant';
  text: string;
}

/** Caps, mirrored (and enforced) by the backend's PriorTurn model. */
export const PRIOR_MAX_TURNS = 20;
export const PRIOR_TURN_MAX_CHARS = 1_000;
export const PRIOR_TOTAL_MAX_CHARS = 8_000;

/** A gap this long since the last finished reply means the server memory may be gone. */
export const PRIOR_IDLE_MS = 10 * 60 * 1000;

function turnText(m: Message): string {
  if (m.role === 'user') return (m.displayContent || m.content || '').trim();
  const fromParts = (m.parts || [])
    .filter((p) => p.type === 'text' && p.content)
    .map((p) => p.content as string)
    .join('\n');
  return (fromParts || m.content || '').trim();
}

/**
 * The finished user/assistant turns before the message being sent, newest kept, capped.
 * `pendingText` is the message about to go out: when it is already the trailing user bubble
 * (it is saved before the POST), it is left out so it is not sent twice.
 */
export function buildPriorConversation(messages: Message[], pendingText?: string): PriorTurn[] {
  const kept = messages.filter((m) =>
    (m.role === 'user' || m.role === 'assistant')
    && !m.isClientOnly && !m.isCommittedWelcome && !m.isSystemMarker && !m.isSyntheticTurn
    && !m.inHumanZone && !m.isStreaming && !m.hasError);
  const last = kept[kept.length - 1];
  if (last && last.role === 'user' && pendingText !== undefined
      && (last.content || '').trim() === pendingText.trim()) {
    kept.pop();
  }
  const turns: PriorTurn[] = [];
  let total = 0;
  for (let i = kept.length - 1; i >= 0 && turns.length < PRIOR_MAX_TURNS; i--) {
    const text = safeRedact(turnText(kept[i])).text.slice(0, PRIOR_TURN_MAX_CHARS);
    if (!text) continue;
    if (total + text.length > PRIOR_TOTAL_MAX_CHARS) break;
    total += text.length;
    turns.unshift({ role: kept[i].role as PriorTurn['role'], text });
  }
  return turns;
}

/** When the last finished assistant reply is older than PRIOR_IDLE_MS (or there is none). */
export function idleSinceLastReply(messages: Message[], now: number = Date.now()): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === 'assistant' && !m.isClientOnly && !m.isStreaming) {
      const t = new Date(m.timestamp).getTime();
      return !Number.isFinite(t) || now - t > PRIOR_IDLE_MS;
    }
  }
  return false;
}
